import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export const MessagesFab = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }

    const load = async () => {
      // Count replies on this user's messages that aren't read yet
      const { data: msgs } = await supabase
        .from("messages")
        .select("id")
        .eq("user_id", user.id);
      const ids = (msgs ?? []).map((m: any) => m.id);
      if (!ids.length) {
        setUnread(0);
        return;
      }
      const { count } = await supabase
        .from("message_replies")
        .select("id", { count: "exact", head: true })
        .in("message_id", ids)
        .is("read_at", null);
      setUnread(count ?? 0);
    };
    load();

    const ch = supabase
      .channel("fab-replies")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "message_replies" },
        () => {
          load();
          setPulse(true);
          setTimeout(() => setPulse(false), 1800);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(ch);
    };
  }, [user]);

  if (!user) return null;
  if (/^\/(checkout|order-confirmation)/.test(location.pathname)) return null;

  const hasUnread = unread > 0;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to="/my-messages"
            aria-label="Messages"
            title="Messages"
            style={{ bottom: "calc(1.25rem + 48px + 14px + env(safe-area-inset-bottom))" }}
            className={cn(
              "fixed right-4 md:right-6 z-40 grid h-12 w-12 md:h-14 md:w-14 place-items-center rounded-full",
              "bg-gradient-primary text-primary-foreground shadow-card transition-smooth hover:scale-105 active:scale-95",
              "md:[bottom:calc(1.5rem+56px+16px+env(safe-area-inset-bottom))]",
              hasUnread && "ring-2 ring-primary/50 shadow-glow",
              pulse && "animate-[pulse_1.6s_ease-out_2]",
            )}
          >
            <MessageCircle className="h-5 w-5 md:h-6 md:w-6" />
            {hasUnread && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold animate-fade-in">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
            {hasUnread && (
              <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping opacity-60" />
            )}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="left">Messages{hasUnread ? ` · ${unread} new` : ""}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
